/**
 * PostHog Analytics Provider
 * Implements the AnalyticsProvider interface for PostHog
 */

import type { AnalyticsProvider } from "@artha/types";

class PostHogProviderError extends Error {
  constructor(
    public readonly providerName: string,
    message: string,
    public readonly originalError?: Error
  ) {
    super(`[${providerName}] ${message}`);
    this.name = "PostHogProviderError";
  }
}

const PH_TAG = "[PostHog]";
const INSTANCE_TIMEOUT_MS = 10_000;

export class PostHogProvider implements AnalyticsProvider {
  readonly name = "posthog";
  readonly enabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private posthog: any = null;
  private instancePromise: Promise<void>;
  private resolveInstance!: () => void;

  constructor(enabled: boolean = true) {
    this.enabled = enabled;
    this.instancePromise = new Promise<void>((resolve) => {
      this.resolveInstance = resolve;
    });
  }

  /**
   * Set PostHog instance (called from React hook).
   * Resolves the internal promise so queued calls can proceed.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setPostHogInstance(posthog: any): void {
    this.posthog = posthog;
    if (posthog) {
      this.resolveInstance();
    }
  }

  /**
   * Wait for the PostHog instance to be available (with timeout).
   */
  private async ensureReady(): Promise<boolean> {
    if (this.posthog) return true;

    try {
      await Promise.race([
        this.instancePromise,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("PostHog instance timeout")), INSTANCE_TIMEOUT_MS)
        ),
      ]);
      return !!this.posthog;
    } catch {
      console.warn(PH_TAG, "Instance not available within timeout");
      return false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async identify(userId: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;

    if (!(await this.ensureReady())) {
      console.warn(PH_TAG, "Not ready, dropping identify for:", userId);
      return;
    }

    try {
      this.posthog.identify(userId, properties);
      if (properties && this.posthog.people) {
        this.posthog.people.set(properties);
      }
    } catch (error) {
      throw new PostHogProviderError(this.name, "Failed to identify user", error as Error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async track(eventName: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;

    if (!(await this.ensureReady())) {
      console.warn(PH_TAG, "Not ready, dropping event:", eventName);
      return;
    }

    try {
      this.posthog.capture(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      throw new PostHogProviderError(this.name, "Failed to track event", error as Error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async page(pageName?: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;
    if (!(await this.ensureReady())) return;

    try {
      if (pageName) {
        this.posthog.capture("$pageview", {
          $current_url: window.location.href,
          page_name: pageName,
          ...properties,
        });
      }
    } catch (error) {
      throw new PostHogProviderError(this.name, "Failed to track page view", error as Error);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async group(groupType: string, groupId: string, properties?: Record<string, any>): Promise<void> {
    if (!this.enabled) return;
    if (!(await this.ensureReady())) return;

    try {
      this.posthog.group(groupType, groupId, properties);
    } catch (error) {
      throw new PostHogProviderError(this.name, "Failed to set group", error as Error);
    }
  }

  async alias(newId: string, previousId?: string): Promise<void> {
    if (!this.enabled) return;
    if (!(await this.ensureReady())) return;

    try {
      this.posthog.alias(newId, previousId);
    } catch (error) {
      throw new PostHogProviderError(this.name, "Failed to alias user", error as Error);
    }
  }

  async reset(): Promise<void> {
    if (!this.enabled || !this.posthog) return;

    try {
      this.posthog.reset(true);
    } catch (error) {
      throw new PostHogProviderError(this.name, "Failed to reset", error as Error);
    }
  }

  isFeatureEnabled(flag: string): boolean {
    if (!this.enabled || !this.posthog) return false;
    return this.posthog.isFeatureEnabled(flag);
  }

  getFeatureFlag(flag: string): string | boolean | undefined {
    if (!this.enabled || !this.posthog) return undefined;
    return this.posthog.getFeatureFlag(flag);
  }

  isProviderReady(): boolean {
    return !!this.posthog;
  }
}

export function createPostHogProvider(enabled: boolean = true): PostHogProvider {
  return new PostHogProvider(enabled);
}
